import React from "react";
import ServicePageTemplate, { BenefitItem, ProcessItem, WhyChooseItem } from "../Components/service-template/service-template";
import genAi from "../Assets/image/gen-ai-solution.webp";
import { MessageSquare, Image, Award, Search, GitBranch, Key, Map, Layout, Settings, Rocket } from "lucide-react";

function GenerativeAiSolutions() {
  const benefitsList: BenefitItem[] = [
    {
      title: "Context-Aware RAG",
      description: "Connect your enterprise document vaults directly to LLMs to query data, retrieve citations, and answer questions accurately.",
    },
    {
      title: "Domain Fine-Tuning",
      description: "Fine-tune open-source models like Llama or Mistral to reflect your brand tone, terminology, and formatting constraints.",
    },
    {
      title: "Creative Content Engines",
      description: "Automate code drafting, marketing copywriting, visual design generations, and report writing pipelines.",
    },
    {
      title: "Advanced Semantic Indexing",
      description: "Implementing hybrid lexical/dense retrieval pipelines combined with neural cross-encoder re-ranking for unmatched accuracy.",
    },
    {
      title: "Hallucination Safeguards",
      description: "Rigorous guardrail middleware that checks generated answers for factual accuracy, toxic contents, and data leaks.",
    },
    {
      title: "Trace Observability Logs",
      description: "Integration with analytical tracing software tracking model prompts, cost metrics, token logs, and user reviews.",
    },
  ];

  const processList: ProcessItem[] = [
    {
      step: 1,
      title: "Data Audit & Feasibility",
      icon: <Map />,
      description: "We review your data sources, vector search viability, and model compatibility options.",
    },
    {
      step: 2,
      title: "RAG Pipeline Engineering",
      icon: <Layout />,
      description: "Chunking, embedding, and storing data in optimized vector databases with semantic re-ranking.",
    },
    {
      step: 3,
      title: "Model Instruction Tuning",
      icon: <Settings />,
      description: "Writing prompt templates, configuring hyper-parameters, and fine-tuning models on domain datasets.",
    },
    {
      step: 4,
      title: "Scalable Inference Deployment",
      icon: <Rocket />,
      description: "Setting up fast inference endpoints with caching layers to keep response times under 1 second.",
    },
  ];

  const whyChooseList: WhyChooseItem[] = [
    {
      id: 1,
      title: "Custom Conversational UIs",
      description: "We build intuitive, fluid chat interfaces equipped with streaming responses, source citing, and file uploads.",
      icon: <MessageSquare size={24} />,
    },
    {
      id: 2,
      title: "Generative Media Capabilities",
      description: "Seamless integration of Stable Diffusion, Midjourney, and Dall-E pipelines for automated graphic asset creation.",
      icon: <Image size={24} />,
    },
    {
      id: 3,
      title: "Hallucination Control",
      description: "Advanced verification guardrails (like LlamaGuard) to check responses for factual truth and safety compliance.",
      icon: <Award size={24} />,
    },
    {
      id: 4,
      title: "Hybrid Search Engines",
      description: "Merging BM25 keyword search with dense vector matching (Dense + Sparse) to ensure 99% relevance on user queries.",
      icon: <Search size={24} />,
    },
    {
      id: 5,
      title: "Open-Source LLM Expertise",
      description: "Deploying and managing self-hosted models locally or on private cloud endpoints for absolute data privacy.",
      icon: <GitBranch size={24} />,
    },
    {
      id: 6,
      title: "Enterprise Key Management",
      description: "Secure credential handling, token quotas, and system usage logging to track your organizational API expenditure.",
      icon: <Key size={24} />,
    },
  ];

  return (
    <ServicePageTemplate
      metaTitle="Generative AI Solutions | Softcoding Solutions"
      metaDescription="Deploy custom Generative AI solutions. From RAG document search engines to fine-tuned domain LLMs and creative asset generation pipelines."
      canonicalUrl="https://softcodingsolutions.com/generative-ai-solutions-company"
      heroTitle="Generative AI Solutions"
      heroSubtitle="Transform raw data into active value using custom Generative AI architectures, semantic search pipelines, and tailored language models."
      heroImage={genAi}
      benefitsTitle="benefits of generative AI"
      benefitsHeading="Automate knowledge retrieval and creative generation with business-aligned generative systems."
      benefitsList={benefitsList}
      processTitle="our generative process"
      processDescription="From raw data auditing to optimized, low-latency model inference deployments—engineered for accuracy."
      processList={processList}
      processImage={genAi}
      whyChooseTitle="Why Softcoding Solutions for Gen AI"
      whyChooseList={whyChooseList}
      contactHeading="Ready to implement Generative AI?"
      contactDescription="Discuss your generative use-cases with our developers, and let's craft a secure, contextual AI system that elevates your operations."
      contactPurpose="Gen AI Solutions"
      contactFormTitle="Hire Gen AI Developer"
    />
  );
}

export default GenerativeAiSolutions;
